import { z } from 'zod';
import { ErrorCode } from '../../shared/error-codes';
import { emailSchema } from '../../shared/email.schema';
import { SignInResponseSchema } from './signed-in-session.schema';
/**
 * SSO Start Request Schema
 * POST /api/v2/auth/sso/start
 */ export const SsoStartRequestSchema = z.object({
    email: emailSchema()
});
/**
 * SSO Start Response Schema
 * `sso` carries the Zitadel authorization URL the frontend redirects to;
 * `unknown` means the email resolved to no workspace — deliberately the same
 * answer as an unrecognized email to avoid account enumeration.
 */ export const SsoStartResponseSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('sso'),
        authorizationUrl: z.string()
    }),
    z.object({
        mode: z.literal('unknown')
    })
]);
/**
 * SSO Callback Request Schema
 * POST /api/v2/auth/sso/callback
 */ export const SsoCallbackRequestSchema = z.object({
    code: z.string().min(1, ErrorCode.FIELD_REQUIRED),
    state: z.string().min(1, ErrorCode.FIELD_REQUIRED)
});
/**
 * SSO Callback Response Schema
 * A successful callback establishes the signed-in session. The raw OIDC
 * id_token never reaches the browser: it is personal data (email, name, sub
 * are base64-decodable), so it is stored server-side on the auth session and
 * the logout end_session URL is built from there.
 */ export const SsoSignInResponseSchema = SignInResponseSchema.extend({
    /**
   * Echoed from the step-up state so the callback page can send the user
   * back to where the 403 (AUTH_REAUTH_REQUIRED) interrupted them.
   */ returnTo: z.string().optional()
});
/**
 * SSO Step-Up Request Schema
 * POST /api/v2/auth/sso/step-up
 * `returnTo` is relative-path-only because the frontend redirects the browser
 * to it verbatim after the step-up callback: a single leading slash followed
 * by neither '/' nor '\' — WHATWG URL parsing folds '\' into '/', so both
 * '//evil.com' and '/\evil.com' would resolve protocol-relative. Tab/CR/LF
 * are banned anywhere because WHATWG parsing strips them BEFORE the slash
 * folding, so '/\t/evil.com' would otherwise collapse into '//evil.com'.
 */ export const SsoStepUpRequestSchema = z.object({
    returnTo: z.string().max(512).regex(/^\/(?![/\\])[^\t\n\r]*$/, ErrorCode.FIELD_REQUIRED).optional()
});
/**
 * SSO Step-Up Response Schema
 * No 'unknown' arm: the caller already holds an SSO session.
 */ export const SsoStepUpResponseSchema = z.object({
    authorizationUrl: z.string()
});
/**
 * SSO Logout Request/Response Schemas
 * POST /api/v2/auth/sso/logout
 * The request carries no body: the id_token_hint is the one stored on the
 * server-side auth session (looked up via the HttpOnly session cookie), never
 * supplied by the client. `logoutUrl` is Zitadel's end_session URL (comes back
 * to the login page); null when no identity provider is configured — the
 * frontend then just navigates to /login locally.
 */ export const SsoLogoutRequestSchema = z.object({});
export const SsoLogoutResponseSchema = z.object({
    logoutUrl: z.string().nullable()
});

//# sourceMappingURL=auth-sso.schema.js.map